import { useCallback } from 'react'
import { useRouter } from 'next/router'

type QueryPatch = Record<string, string | null | undefined>

// Filters live in the address bar so links between pages can open a specific class, date or student.
export const useQueryParams = (): {
    ready: boolean
    get: (key: string) => string | undefined
    set: (patch: QueryPatch, options?: { push?: boolean }) => void
} => {
    const router = useRouter()

    const get = useCallback((key: string): string | undefined => {
        const value = router.query[key]

        return Array.isArray(value) ? value[0] : value
    }, [router.query])

    const set = useCallback((patch: QueryPatch, options?: { push?: boolean }): void => {
        const query: Record<string, string> = {}

        Object.entries(router.query).forEach(([key, value]) => {
            if (typeof value === 'string') query[key] = value
        })

        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') delete query[key]
            else query[key] = value
        })

        const target = { pathname: router.pathname, query }
        const transition = { shallow: true, scroll: false }

        if (options?.push) void router.push(target, undefined, transition)
        else void router.replace(target, undefined, transition)
    }, [router])

    return { ready: router.isReady, get, set }
}
